import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import {
  buildMyDayView,
  type MyDayInspection,
  type MyDayView,
} from "@/components/dashboard/roleDashboard.helpers";

/**
 * „Mein Tag"-Daten für das tester-Dashboard (TES-1093):
 * die MIR zugewiesenen offenen Prüfungen (pending/in_progress) mit Fälligkeit.
 *
 * RLS bleibt die Sicherheitsgrenze (user-JWT-scoped Client); der
 * assigned_to-Filter ist nur die fachliche Sicht, keine Security.
 */

const myDayKeys = {
  all: ["my-day"] as const,
  list: (userId?: string) => [...myDayKeys.all, userId] as const,
};

const MY_DAY_SELECT =
  "id, status, due_date, created_at, checklists(name, version), inspection_positions(name)";

async function fetchMyDayInspections(userId: string): Promise<MyDayInspection[]> {
  const { data, error } = await supabase
    .from("inspections")
    .select(MY_DAY_SELECT)
    .eq("assigned_to", userId)
    .in("status", ["pending", "in_progress"])
    .order("due_date", { ascending: true, nullsFirst: false })
    .limit(50);

  if (error) throw error;
  return (data ?? []) as MyDayInspection[];
}

export function useMyDayQuery(userId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: myDayKeys.list(userId),
    queryFn: () => fetchMyDayInspections(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });

  // Realtime: Prüfungs-Änderungen invalidieren die Tagessicht (zentraler Push).
  useRealtimeSubscription(
    ["inspections"],
    () => {
      queryClient.invalidateQueries({ queryKey: myDayKeys.all });
    },
    { enabled: !!userId, debounceMs: 1000 },
  );

  const view: MyDayView = buildMyDayView(query.data ?? []);

  return {
    view,
    isLoading: query.isLoading,
    error: query.error,
  };
}
